import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, FileDown, FileSpreadsheet, Plus, Minus, Keyboard } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, ErrorBar,
} from "recharts";
import Layout from "@/components/Layout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ORANGE = "#FF8A50";
const ORANGE_DARK = "#E65C00";

const defaultManualData = () => ({
  headers: ["", "", ""],
  rows: Array.from({ length: 8 }, () => ["", "", ""]),
});

const placeholderFor = (ci) => `Amostra ${String.fromCharCode(65 + ci)}`;

const TEST_NOTES = {
  "ADQ (Análise Descritiva Quantitativa)":
    "Recomendado para provadores com o mesmo nível de treinamento. Não recomendado para provadores não treinados.",
  "PDO (Perfil Descritivo Otimizado)":
    "Recomendado para provadores com o mesmo nível de treinamento. Não recomendado para provadores não treinados.",
  "Perfil Flash":
    "Recomendado para provadores com o mesmo nível de treinamento/conhecimento.",
  "Comparação Múltipla":
    "Recomendado para provadores com o mesmo nível de treinamento. Não recomendado para provadores não treinados.",
};
const TEST_NOTE_DEFAULT =
  "Não recomendado para provadores treinados ou envolvidos no projeto.";

export default function Anova() {
  const [meta, setMeta] = useState(null);
  const [analysisTitle, setAnalysisTitle] = useState("");
  const [category, setCategory] = useState("descriptive");
  const [testName, setTestName] = useState("");
  const [postHoc, setPostHoc] = useState("tukey");
  const [controlGroup, setControlGroup] = useState("");
  const [confidence, setConfidence] = useState("0.95");
  const [file, setFile] = useState(null);
  const [inputMode, setInputMode] = useState("file"); // "file" or "manual"
  const [manualData, setManualData] = useState(defaultManualData);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);

  useEffect(() => {
    api.get("/anova/test-types").then((r) => setMeta(r.data.categories)).catch(() => {});
  }, []);

  const availableTests = useMemo(() => meta?.[category]?.tests || [], [meta, category]);
  const minPanelistsMap = meta?.[category]?.min_panelists;
  const minPanelists = minPanelistsMap
    ? (typeof minPanelistsMap === "object" ? minPanelistsMap[confidence] : minPanelistsMap)
    : null;

  useEffect(() => {
    if (availableTests.length && !availableTests.includes(testName)) {
      setTestName(availableTests[0]);
    }
  }, [availableTests, testName]);

  const buildManualFile = () => {
    // Validate: at least 2 columns and at least 2 non-empty numeric rows per column
    const { headers, rows } = manualData;
    if (headers.length < 2) throw new Error("São necessárias no mínimo 2 colunas (amostras/atributos).");
    // Missing header names fall back to the visual placeholder ('Amostra A', 'Amostra B', ...)
    const effectiveHeaders = headers.map((h, i) => (h && h.trim()) ? h.trim() : placeholderFor(i));
    // At least 2 rows with all values numeric
    let filledRows = 0;
    for (const row of rows) {
      let ok = true;
      for (const v of row) {
        if (v === "" || v === null || isNaN(Number(String(v).replace(",", ".")))) { ok = false; break; }
      }
      if (ok) filledRows++;
    }
    if (filledRows < 2) throw new Error("Preencha pelo menos 2 linhas completas com valores numéricos.");

    const escape = (s) => {
      const str = String(s ?? "");
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [effectiveHeaders.map(escape).join(",")];
    for (const row of rows) {
      const normalized = row.map((v) => (v === "" || v == null) ? "" : String(v).replace(",", "."));
      lines.push(normalized.map(escape).join(","));
    }
    const csv = lines.join("\n");
    return new File([new Blob([csv], { type: "text/csv" })], "dados_digitados.csv", { type: "text/csv" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let payloadFile = file;
      if (inputMode === "manual") {
        try { payloadFile = buildManualFile(); }
        catch (err) { toast.error(err.message); setLoading(false); return; }
      } else if (!payloadFile) {
        toast.error("Selecione um arquivo Excel ou CSV"); setLoading(false); return;
      }
      const fd = new FormData();
      fd.append("file", payloadFile);
      fd.append("test_category", category);
      fd.append("test_name", testName);
      fd.append("post_hoc", postHoc);
      fd.append("control_group", controlGroup || "");
      fd.append("confidence", confidence);
      if (analysisTitle.trim()) fd.append("title", analysisTitle.trim());
      const { data } = await api.post("/anova/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data.result);
      setAnalysisId(data.id);
      toast.success("Análise concluída");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (kind) => {
    if (!analysisId) return;
    const token = localStorage.getItem("sensorize_token");
    const url = `${API_BASE}/anova/${analysisId}/export/${kind}${token ? `?_t=${Date.now()}` : ""}`;
    // Use fetch to send credentials and download blob
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error("Falha ao exportar");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `anova_${analysisId}.${kind === "pdf" ? "pdf" : "xlsx"}`;
        a.click();
      })
      .catch((e) => toast.error(e.message));
  };

  const boxplotData = useMemo(() => {
    if (!result) return [];
    return result.descriptives.map((d) => ({
      group: d.group,
      min: d.min, q1: d.q1, median: d.median, q3: d.q3, max: d.max,
      mean: d.mean, sem: d.sem,
      lowWhisker: d.min, highWhisker: d.max,
    }));
  }, [result]);

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#FF8A50]">Testes com ANOVA</p>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900 mt-1">
            Análise de Variância
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Envie um arquivo CSV/Excel com <strong>colunas = grupos</strong> e <strong>linhas = provadores</strong>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar */}
        <Card className="lg:col-span-4 border-slate-200 h-fit">
          <CardContent className="p-6">
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">Configuração</h2>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="anova-title">Nome do teste <span className="text-slate-400 font-normal">(opcional)</span></Label>
                <Input
                  id="anova-title"
                  value={analysisTitle}
                  onChange={(e) => setAnalysisTitle(e.target.value)}
                  placeholder="Ex.: Lote 42 · Sabor cereja"
                  data-testid="anova-title-input"
                  maxLength={120}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Tipo de teste</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="anova-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="descriptive">Descritivo</SelectItem>
                    <SelectItem value="discriminative">Discriminativo</SelectItem>
                    <SelectItem value="affective">Afetivo</SelectItem>
                  </SelectContent>
                </Select>
                {minPanelists != null && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="anova-min-panelists">
                    Provadores mínimos sugeridos ({(parseFloat(confidence)*100).toFixed(0)}%): <strong>{minPanelists}</strong>
                  </p>
                )}
              </div>

              <div>
                <Label>Nível de confiança</Label>
                <Select value={confidence} onValueChange={setConfidence}>
                  <SelectTrigger data-testid="anova-confidence-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.90">90% (α = 0,10)</SelectItem>
                    <SelectItem value="0.95">95% (α = 0,05)</SelectItem>
                    <SelectItem value="0.99">99% (α = 0,01)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Método</Label>
                <Select value={testName} onValueChange={setTestName}>
                  <SelectTrigger data-testid="anova-test-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {availableTests.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {testName && (
                  <p
                    className="text-xs text-slate-600 italic mt-1.5 leading-snug"
                    data-testid="anova-test-note"
                  >
                    {TEST_NOTES[testName] || TEST_NOTE_DEFAULT}
                  </p>
                )}
              </div>

              <div>
                <Label>Pós-teste</Label>
                <Select value={postHoc} onValueChange={setPostHoc}>
                  <SelectTrigger data-testid="anova-posthoc-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tukey">Tukey HSD (comparações par-a-par)</SelectItem>
                    <SelectItem value="dunnett">Dunnett (contra controle)</SelectItem>
                    <SelectItem value="none">Nenhum</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {postHoc === "dunnett" && (
                <div>
                  <Label>Grupo controle (nome da coluna)</Label>
                  <Input
                    value={controlGroup}
                    onChange={(e) => setControlGroup(e.target.value)}
                    placeholder="ex: Controle"
                    data-testid="anova-control-input"
                  />
                </div>
              )}

              <div>
                <Label>Dados</Label>
                <Tabs value={inputMode} onValueChange={setInputMode} className="mt-1">
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="file" data-testid="input-mode-file">
                      <UploadCloud className="w-4 h-4 mr-1" /> Enviar arquivo
                    </TabsTrigger>
                    <TabsTrigger value="manual" data-testid="input-mode-manual">
                      <Keyboard className="w-4 h-4 mr-1" /> Digitar dados
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="file" className="mt-3">
                    <label className="flex flex-col items-center justify-center border border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:bg-slate-50 transition-colors">
                      <UploadCloud className="w-6 h-6 text-slate-400 mb-2" />
                      <span className="text-sm text-slate-600">
                        {file ? file.name : "Clique para selecionar (.csv, .xlsx)"}
                      </span>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        data-testid="anova-file-input"
                      />
                    </label>
                    <p className="text-xs text-slate-500 mt-2">Colunas = amostras/atributos, linhas = notas dos provadores.</p>
                  </TabsContent>

                  <TabsContent value="manual" className="mt-3">
                    <ManualDataGrid data={manualData} onChange={setManualData} />
                    <p className="text-xs text-slate-500 mt-2">Digite o nome de cada coluna (amostra/atributo) e as notas dos provadores em cada linha.</p>
                  </TabsContent>
                </Tabs>
              </div>

              <Button
                type="submit" disabled={loading}
                data-testid="anova-analyze-button"
                className="w-full bg-[#FF8A50] hover:bg-[#E65C00] text-white font-semibold"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Analisar
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="lg:col-span-8 space-y-6">
          {!result && (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="p-10 text-center text-slate-500">
                Envie um arquivo para visualizar os resultados.
              </CardContent>
            </Card>
          )}

          {result && (
            <>
              <Card className="border-slate-200" data-testid="anova-summary-card">
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-heading font-bold text-slate-900">Resumo ANOVA</h2>
                      <p className="text-sm text-slate-500">One-way ANOVA — α = {result.anova.alpha}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => downloadFile("pdf")} data-testid="anova-export-pdf">
                        <FileDown className="w-4 h-4 mr-1" /> PDF
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => downloadFile("excel")} data-testid="anova-export-excel">
                        <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <Stat label="F" value={result.anova.F.toFixed(4)} />
                    <Stat label="p-valor" value={result.anova.p_value.toExponential(3)} />
                    <Stat label="gl entre / dentro" value={`${result.anova.df_between} / ${result.anova.df_within}`} />
                    <Stat
                      label="Resultado"
                      value={
                        <Badge className={result.anova.significant ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
                          {result.anova.significant ? "Significativo" : "Não significativo"}
                        </Badge>
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              <Tabs defaultValue="charts">
                <TabsList>
                  <TabsTrigger value="charts" data-testid="tab-charts">Gráficos</TabsTrigger>
                  <TabsTrigger value="results-table" data-testid="tab-results-table">Tabela de Resultados</TabsTrigger>
                  <TabsTrigger value="descriptives" data-testid="tab-descriptives">Descritiva</TabsTrigger>
                  <TabsTrigger value="assumptions" data-testid="tab-assumptions">Premissas</TabsTrigger>
                  <TabsTrigger value="posthoc" data-testid="tab-posthoc">Pós-teste</TabsTrigger>
                </TabsList>

                <TabsContent value="charts">
                  <Card className="border-slate-200"><CardContent className="p-6">
                    <h3 className="font-heading font-semibold text-slate-900 mb-4">Médias por grupo (± EPM)</h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={result.descriptives}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="group" stroke="#475569" />
                          <YAxis stroke="#475569" />
                          <Tooltip
                            contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8 }}
                          />
                          <Bar dataKey="mean" fill={ORANGE} radius={[6, 6, 0, 0]}>
                            <ErrorBar dataKey="sem" width={4} strokeWidth={2} stroke={ORANGE_DARK} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <h3 className="font-heading font-semibold text-slate-900 mt-8 mb-4">Amplitudes (min → max) com mediana</h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={boxplotData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="group" stroke="#475569" />
                          <YAxis stroke="#475569" />
                          <Tooltip
                            contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8 }}
                            formatter={(value, name, p) => {
                              const d = p.payload;
                              return [`min ${d.min.toFixed(2)} · Q1 ${d.q1.toFixed(2)} · med ${d.median.toFixed(2)} · Q3 ${d.q3.toFixed(2)} · max ${d.max.toFixed(2)}`, "resumo"];
                            }}
                          />
                          <Bar dataKey="median" fill={ORANGE} radius={[4, 4, 0, 0]}>
                            <ErrorBar
                              dataKey={(d) => [Math.max(0, d.median - d.min), Math.max(0, d.max - d.median)]}
                              width={16} strokeWidth={2} stroke={ORANGE_DARK}
                            />
                          </Bar>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent></Card>
                </TabsContent>

                <TabsContent value="results-table">
                  <Card className="border-slate-200"><CardContent className="p-6">
                    <h3 className="font-heading font-semibold text-slate-900 mb-3">
                      Tabela de Resultados
                    </h3>
                    <ResultsTable
                      descriptives={result.descriptives}
                      postHoc={result.post_hoc}
                      attribute={analysisTitle || testName || "Atributo"}
                      alpha={result.anova.alpha}
                    />
                  </CardContent></Card>
                </TabsContent>

                <TabsContent value="descriptives">
                  <Card className="border-slate-200"><CardContent className="p-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Grupo</TableHead><TableHead>n</TableHead>
                          <TableHead>Média</TableHead><TableHead>DP</TableHead>
                          <TableHead>Mín</TableHead><TableHead>Q1</TableHead>
                          <TableHead>Mediana</TableHead><TableHead>Q3</TableHead>
                          <TableHead>Máx</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.descriptives.map((d) => (
                          <TableRow key={d.group}>
                            <TableCell className="font-semibold">{d.group}</TableCell>
                            <TableCell>{d.n}</TableCell>
                            <TableCell>{d.mean.toFixed(3)}</TableCell>
                            <TableCell>{d.std.toFixed(3)}</TableCell>
                            <TableCell>{d.min.toFixed(3)}</TableCell>
                            <TableCell>{d.q1.toFixed(3)}</TableCell>
                            <TableCell>{d.median.toFixed(3)}</TableCell>
                            <TableCell>{d.q3.toFixed(3)}</TableCell>
                            <TableCell>{d.max.toFixed(3)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent></Card>
                </TabsContent>

                <TabsContent value="assumptions">
                  <Card className="border-slate-200"><CardContent className="p-6 space-y-6">
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900 mb-3">Shapiro-Wilk (Normalidade)</h3>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Grupo</TableHead><TableHead>W</TableHead>
                          <TableHead>p-valor</TableHead><TableHead>Normal?</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {result.shapiro.map((s, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-semibold">{s.group}</TableCell>
                              <TableCell>{s.W != null ? s.W.toFixed(4) : "—"}</TableCell>
                              <TableCell>{s.p_value != null ? s.p_value.toExponential(3) : "—"}</TableCell>
                              <TableCell>
                                {s.normal == null ? <span className="text-xs">{s.note}</span> :
                                  <Badge className={s.normal ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                                    {s.normal ? "Sim" : "Não"}
                                  </Badge>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div>
                      <h3 className="font-heading font-semibold text-slate-900 mb-3">Levene (Homogeneidade de variâncias)</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <Stat label="Estatística" value={result.levene.statistic.toFixed(4)} />
                        <Stat label="p-valor" value={result.levene.p_value.toExponential(3)} />
                        <Stat
                          label="Homogêneas?"
                          value={
                            <Badge className={result.levene.homogeneous ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                              {result.levene.homogeneous ? "Sim" : "Não"}
                            </Badge>
                          }
                        />
                      </div>
                    </div>
                  </CardContent></Card>
                </TabsContent>

                <TabsContent value="posthoc">
                  <Card className="border-slate-200"><CardContent className="p-6">
                    <h3 className="font-heading font-semibold text-slate-900 mb-3">Pós-teste: {result.post_hoc.type}</h3>
                    {result.post_hoc.results.length === 0 ? (
                      <p className="text-slate-500">Nenhum pós-teste executado.</p>
                    ) : (
                      <Table>
                        <TableHeader><TableRow>
                          {"group1" in result.post_hoc.results[0] ? (
                            <>
                              <TableHead>Grupo 1</TableHead><TableHead>Grupo 2</TableHead>
                              <TableHead>Δ Médias</TableHead><TableHead>IC 95%</TableHead>
                              <TableHead>p-valor</TableHead><TableHead>Sig.</TableHead>
                            </>
                          ) : (
                            <>
                              <TableHead>Grupo</TableHead><TableHead>Controle</TableHead>
                              <TableHead>Estatística</TableHead><TableHead>p-valor</TableHead>
                              <TableHead>Sig.</TableHead>
                            </>
                          )}
                        </TableRow></TableHeader>
                        <TableBody>
                          {result.post_hoc.results.map((r, i) => (
                            <TableRow key={i}>
                              {"group1" in r ? (
                                <>
                                  <TableCell className="font-semibold">{r.group1}</TableCell>
                                  <TableCell className="font-semibold">{r.group2}</TableCell>
                                  <TableCell>{r.mean_diff.toFixed(3)}</TableCell>
                                  <TableCell>[{r.ci_low.toFixed(3)}, {r.ci_high.toFixed(3)}]</TableCell>
                                  <TableCell>{r.p_value.toExponential(3)}</TableCell>
                                  <TableCell>
                                    <Badge className={r.significant ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
                                      {r.significant ? "Sim" : "Não"}
                                    </Badge>
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell className="font-semibold">{r.group}</TableCell>
                                  <TableCell className="font-semibold">{r.control}</TableCell>
                                  <TableCell>{r.statistic.toFixed(3)}</TableCell>
                                  <TableCell>{r.p_value.toExponential(3)}</TableCell>
                                  <TableCell>
                                    <Badge className={r.significant ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
                                      {r.significant ? "Sim" : "Não"}
                                    </Badge>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent></Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 p-3 bg-white">
      <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-heading font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

// Superscript unicode letters used to render the CLD group letters (ᵃ, ᵇ, ᶜ, ...).
const SUP_LETTERS = ["ᵃ","ᵇ","ᶜ","ᵈ","ᵉ","ᶠ","ᵍ","ʰ","ⁱ","ʲ","ᵏ","ˡ","ᵐ","ⁿ","ᵒ","ᵖ","ᵠ","ʳ","ˢ","ᵗ","ᵘ","ᵛ","ʷ","ˣ","ʸ","ᶻ"];

/**
 * Compact Letter Display (CLD) from pairwise significance results.
 * Groups sharing at least one letter are NOT significantly different.
 * Uses a simple greedy assignment on groups sorted by mean descending.
 */
function computeCLD(descriptives, postHocResults) {
  const groups = [...descriptives].sort((a, b) => b.mean - a.mean).map((d) => d.group);
  const notDiff = new Map(); // key "a||b" (sorted) -> true if NOT significantly different
  const setPair = (a, b, val) => {
    const [x, y] = [a, b].sort();
    notDiff.set(`${x}||${y}`, val);
  };
  // Default: assume not different; then override with actual pairwise results
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) setPair(groups[i], groups[j], true);
  }
  for (const r of postHocResults || []) {
    if (r.group1 != null && r.group2 != null) {
      setPair(String(r.group1), String(r.group2), !r.significant);
    } else if (r.group && r.control) {
      // Dunnett-style: only comparisons against control
      setPair(String(r.group), String(r.control), !r.significant);
    }
  }
  const same = (a, b) => (a === b ? true : notDiff.get([a, b].sort().join("||")) === true);

  const letters = new Map(groups.map((g) => [g, new Set()]));
  const alphabet = "abcdefghijklmnopqrstuvwxyz";
  let nextIdx = 0;

  for (const g of groups) {
    for (let li = 0; li < nextIdx; li++) {
      const L = alphabet[li];
      const conflict = groups.some((h) => h !== g && letters.get(h).has(L) && !same(g, h));
      if (!conflict) letters.get(g).add(L);
    }
    if (letters.get(g).size === 0) {
      const L = alphabet[nextIdx++];
      letters.get(g).add(L);
    }
  }
  // Order letters alphabetically per group and convert to superscript
  const result = new Map();
  for (const [g, set] of letters.entries()) {
    const sorted = [...set].sort();
    result.set(g, sorted.map((l) => SUP_LETTERS[alphabet.indexOf(l)]).join(""));
  }
  return result;
}

function ResultsTable({ descriptives, postHoc, attribute, alpha }) {
  const cld = useMemo(
    () => computeCLD(descriptives, postHoc?.results || []),
    [descriptives, postHoc]
  );
  const alphaStr = String(alpha ?? 0.05).replace(".", ",");
  const testRaw = (postHoc?.type || "Tukey").split(" ")[0];
  const testName = testRaw.charAt(0).toUpperCase() + testRaw.slice(1).toLowerCase();
  const groups = descriptives.map((d) => d.group);

  return (
    <div>
      <div className="overflow-x-auto">
        <Table className="border border-slate-200">
          <TableHeader>
            <TableRow>
              <TableHead className="bg-slate-50 font-heading font-semibold text-slate-900">
                Atributo
              </TableHead>
              {groups.map((g) => (
                <TableHead key={g} className="text-center bg-slate-50 font-heading font-semibold text-slate-900">
                  {g}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-semibold text-slate-900">{attribute}</TableCell>
              {descriptives.map((d) => (
                <TableCell key={d.group} className="text-center text-slate-800" data-testid={`cell-${d.group}`}>
                  <span className="font-mono">{d.mean.toFixed(2).replace(".", ",")}</span>
                  <span className="text-slate-600 ml-0.5">{cld.get(d.group)}</span>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-slate-500 italic mt-3" data-testid="results-table-footnote">
        Letras diferentes na linha indicam diferenças significativas pelo teste de {testName} (p&lt;{alphaStr}).
      </p>
    </div>
  );
}

function ManualDataGrid({ data, onChange }) {
  const { headers, rows } = data;
  const MIN_W = 96;

  // Per-column widths (kept local; synced when columns are added/removed)
  const [colWidths, setColWidths] = useState(() => headers.map(() => 120));
  useEffect(() => {
    setColWidths((prev) => {
      if (prev.length === headers.length) return prev;
      if (prev.length < headers.length) {
        return [...prev, ...Array(headers.length - prev.length).fill(120)];
      }
      return prev.slice(0, headers.length);
    });
  }, [headers.length]);

  // Column resize handling: mouse-down on grip, then move updates width.
  const startResize = (colIdx, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const startX = ev.clientX;
    const startW = colWidths[colIdx] ?? 120;
    const onMove = (e) => {
      const next = Math.max(MIN_W, startW + (e.clientX - startX));
      setColWidths((prev) => prev.map((w, i) => (i === colIdx ? next : w)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const updateHeader = (col, value) => {
    const next = { ...data, headers: headers.map((h, i) => (i === col ? value : h)) };
    onChange(next);
  };
  const updateCell = (row, col, value) => {
    // Allow only digits, dot, comma, minus
    const cleaned = value.replace(/[^0-9.,\-]/g, "");
    const next = {
      ...data,
      rows: rows.map((r, i) => (i === row ? r.map((c, j) => (j === col ? cleaned : c)) : r)),
    };
    onChange(next);
  };
  const addColumn = () => {
    const next = {
      headers: [...headers, ""],
      rows: rows.map((r) => [...r, ""]),
    };
    onChange(next);
  };
  const removeColumn = () => {
    if (headers.length <= 2) return;
    onChange({ headers: headers.slice(0, -1), rows: rows.map((r) => r.slice(0, -1)) });
  };
  const addRow = () => {
    onChange({ ...data, rows: [...rows, Array(headers.length).fill("")] });
  };
  const removeRow = () => {
    if (rows.length <= 2) return;
    onChange({ ...data, rows: rows.slice(0, -1) });
  };
  const clearAll = () => {
    onChange({ ...data, rows: rows.map((r) => r.map(() => "")) });
  };

  /**
   * Handle paste from Excel / spreadsheets.
   * anchorRow = -1 means paste into header row (first pasted line becomes headers).
   * Auto-detects tab, semicolon or comma separators. Expands rows/columns as needed.
   * When pasting on a data cell with column 0 as anchor, if the first line is all
   * non-numeric, it is treated as header names.
   */
  const handlePaste = (anchorRow, anchorCol, e) => {
    const text = e.clipboardData?.getData("text");
    if (!text) return;
    // If single-cell content (no tab/newline), let default paste happen.
    if (!/[\t\n;]/.test(text) && text.split(",").length === 1) return;
    e.preventDefault();

    let lines = text.replace(/\r\n?/g, "\n").split("\n");
    while (lines.length && lines[lines.length - 1].trim() === "") lines.pop();
    if (!lines.length) return;

    // Detect delimiter (prefer tab, then semicolon, then comma).
    const first = lines[0];
    const delim = first.includes("\t") ? "\t" : first.includes(";") ? ";" : ",";
    const parsed = lines.map((ln) => ln.split(delim).map((c) => c.trim()));

    const nRows = parsed.length;
    const nCols = Math.max(...parsed.map((r) => r.length));

    let newHeaders = [...headers];
    let newRows = rows.map((r) => [...r]);

    // Expand columns (new headers start empty; placeholder will show 'Amostra X')
    while (newHeaders.length < anchorCol + nCols) {
      newHeaders.push("");
      newRows.forEach((r) => r.push(""));
    }

    // Header-paste path: replace headers first, then data lines below
    let dataStartLine = 0;
    let dataStartRow = anchorRow;
    if (anchorRow === -1) {
      parsed[0].forEach((v, ci) => {
        if (anchorCol + ci < newHeaders.length && v !== "") newHeaders[anchorCol + ci] = v;
      });
      dataStartLine = 1;
      dataStartRow = 0;
    } else {
      // Auto-detect header row when pasting into (row 0, col 0) with all-non-numeric first line
      const firstIsHeader =
        anchorRow === 0 && anchorCol === 0 && parsed.length > 1 &&
        parsed[0].every((v) => v !== "" && isNaN(Number(v.replace(",", "."))));
      if (firstIsHeader) {
        parsed[0].forEach((v, ci) => {
          if (anchorCol + ci < newHeaders.length && v !== "") newHeaders[anchorCol + ci] = v;
        });
        dataStartLine = 1;
      }
    }

    // Expand rows to fit
    const dataLines = parsed.length - dataStartLine;
    while (newRows.length < dataStartRow + dataLines) {
      newRows.push(Array(newHeaders.length).fill(""));
    }

    // Fill cells
    for (let li = dataStartLine; li < parsed.length; li++) {
      const targetRow = dataStartRow + (li - dataStartLine);
      parsed[li].forEach((v, ci) => {
        const targetCol = anchorCol + ci;
        if (targetCol >= newHeaders.length) return;
        // Normalize decimal comma → dot for numeric cells
        const cleaned = v.replace(",", ".");
        newRows[targetRow][targetCol] = isNaN(Number(cleaned)) ? v : cleaned;
      });
    }

    onChange({ headers: newHeaders, rows: newRows });
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="manual-data-grid">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
        <div className="text-xs text-slate-500 font-semibold">
          {headers.length} colunas · {rows.length} linhas
          <span className="ml-2 text-slate-400 font-normal">· dica: cole (Ctrl+V) direto do Excel</span>
        </div>
        <div className="flex items-center gap-1">
          <IconBtn onClick={removeColumn} label="Remover coluna" testId="grid-remove-col" disabled={headers.length <= 2}><Minus className="w-3 h-3" /> Col</IconBtn>
          <IconBtn onClick={addColumn} label="Adicionar coluna" testId="grid-add-col"><Plus className="w-3 h-3" /> Col</IconBtn>
          <IconBtn onClick={removeRow} label="Remover linha" testId="grid-remove-row" disabled={rows.length <= 2}><Minus className="w-3 h-3" /> Linha</IconBtn>
          <IconBtn onClick={addRow} label="Adicionar linha" testId="grid-add-row"><Plus className="w-3 h-3" /> Linha</IconBtn>
          <IconBtn onClick={clearAll} label="Limpar valores" testId="grid-clear">Limpar</IconBtn>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[320px]">
        <table
          className="text-xs border-collapse"
          style={{
            tableLayout: "fixed",
            width: 40 + colWidths.reduce((s, w) => s + w, 0),
          }}
        >
          <colgroup>
            <col style={{ width: 40 }} />
            {colWidths.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>
          <thead className="bg-white sticky top-0 z-10">
            <tr>
              <th className="w-10 px-2 py-1 text-slate-400 font-normal text-right border-b border-slate-200">#</th>
              {headers.map((h, ci) => (
                <th
                  key={ci}
                  className="relative px-1 py-1 border-b border-l border-slate-200"
                >
                  <Input
                    value={h}
                    onChange={(e) => updateHeader(ci, e.target.value)}
                    onPaste={(e) => handlePaste(-1, ci, e)}
                    data-testid={`grid-header-${ci}`}
                    className="h-8 text-xs font-semibold text-slate-900 border-slate-200 placeholder:text-slate-400 placeholder:font-normal"
                    placeholder={placeholderFor(ci)}
                  />
                  {/* resize grip */}
                  <span
                    role="separator"
                    aria-label={`Redimensionar coluna ${ci + 1}`}
                    onMouseDown={(e) => startResize(ci, e)}
                    data-testid={`grid-col-resize-${ci}`}
                    className="absolute top-0 -right-[3px] h-full w-1.5 cursor-col-resize select-none opacity-0 hover:opacity-100 bg-[#FF8A50]/60"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                <td className="w-10 px-2 py-1 text-slate-400 text-right border-b border-slate-100">{ri + 1}</td>
                {row.map((v, ci) => (
                  <td key={ci} className="px-1 py-1 border-b border-l border-slate-100">
                    <input
                      value={v}
                      onChange={(e) => updateCell(ri, ci, e.target.value)}
                      onPaste={(e) => handlePaste(ri, ci, e)}
                      data-testid={`grid-cell-${ri}-${ci}`}
                      inputMode="decimal"
                      className="w-full h-8 px-2 rounded border border-transparent focus:border-[#FF8A50] focus:outline-none text-xs"
                      placeholder="—"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, label, testId, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}
